<script lang="ts" setup>
import { watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { VOCAB_LANGUAGE_GROUPS, type VocabLanguageGroup } from "@/common/types/vocab";
import ImageResponsive from "@/components/ImageResponsive.vue";
import { useSettingsStore } from "@/stores";
import type { LanguageGroup } from "@/types/site";

const { t } = useI18n();

const router = useRouter();
const settingsStore = useSettingsStore();

const isVocabAvailable = (languageGroup: LanguageGroup) =>
  VOCAB_LANGUAGE_GROUPS.some((group) => group === languageGroup);

watch(
  () => settingsStore.activeLanguageGroup,
  (value?: LanguageGroup) => {
    if (!value) {
      return;
    }

    if (!isVocabAvailable(value)) {
      return;
    }

    router.push({ name: "vocab.home" });
  },
  { immediate: true }
);

const languageGroupButtonText = (languageGroup: VocabLanguageGroup) => {
  const translationMap: Record<VocabLanguageGroup, string> = {
    english: t("languageGroups.english.translated"),
    german: t("languageGroups.german.translated")
  };

  return translationMap[languageGroup];
};

const onChangeLanguageGroup = (languageGroup: LanguageGroup) => {
  settingsStore.setActiveLanguageGroup(languageGroup);
};
</script>

<template>
  <div class="vocab-unavailable">
    <ImageResponsive presentational src="/assets/img/vocab/vocab-live.png" />
    <div>
      <h3 class="mb-2 text-center">{{ t("vocabUnavailable.heading") }}</h3>
      <p class="text-center">{{ t("vocabUnavailable.description") }}</p>
      <div class="d-flex align-items-center flex-column">
        <TgButton
          v-for="languageGroup in VOCAB_LANGUAGE_GROUPS"
          :key="languageGroup"
          class="my-3 my-md-2 vocab-unavailable__language-button"
          @click="() => onChangeLanguageGroup(languageGroup)"
        >
          <i18n-t keypath="vocabUnavailable.languageGroupButton">
            <template #languageGroup>{{ languageGroupButtonText(languageGroup) }}</template>
          </i18n-t>
        </TgButton>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.vocab-unavailable {
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  gap: 50px;
  padding: 12px;

  #media-breakpoint-up(md, {
    padding: 0;
  });

  &__language-button {
    width: 100%;

    #media-breakpoint-up(md, {
      width: 40%;
    });
  }
}
</style>
